import React, { memo, useMemo, useCallback } from "react";
import type { MachineEvent } from "@/machines/types/event";
import type { EventSegment } from "../hooks/useTimelineEvents";
import { TimelineEventRect } from "./TimelineEventRect";
import { timeToX } from "../utils/timelineCalculations";
import { createStrokeColor, createGrayedColor, getEventUniqueId } from "../utils/timelineHelpers";
import { SNAPPABLE_EVENT_LAYERS, DIG_LAYER_STATES } from "../config/snapConfig";
import type { VisibleXRange } from "../hooks/useVisibleXRange";

interface TimelineTrackProps {
  track: EventSegment[];
  rowIndex: number;
  yOffset: number;
  rowHeight: number;
  barHeight: number;
  trackKey: string | undefined;
  scaledWidth: number;
  min: number;
  max: number;
  /**
   * The visible slice of the scaled axis. Segments outside it are not rendered
   * — at high zoom the track is hundreds of times wider than the screen, and
   * every off-screen rect still cost a React element and a geometry write on
   * every zoom step. Null renders the whole track (live mode never scrolls).
   */
  visibleRange?: VisibleXRange | null;
  isTrackSelected: boolean;
  isEditMode: boolean;
  selectedEventForEdit: MachineEvent | null;

  // State
  getModifiedEventTimes: (id: string) => { startTime: string; endTime: string } | null;
  hasEventChanges: (id: string) => { type: string } | null;
  isResizing: boolean;
  resizingEvent: MachineEvent | null;
  resizeTempEventTimes: { start: number; end: number } | null;
  lastResizedEventId: string | null;
  isMoving: boolean;
  movingEvent: MachineEvent | null;
  moveTempEventTimes: { start: number; end: number } | null;
  lastMovedEventId: string | null;

  // Event handlers
  onEventEnter: (e: React.MouseEvent<SVGRectElement>, seg: EventSegment) => void;
  onEventLeave: () => void;
  onEventClick: (event: MachineEvent) => void;
  /** Non-edit-mode click on a segment — opens the event's detail (e.g. the map's on-map card). */
  onEventOpen?: (event: MachineEvent, isTruckCycle: boolean) => void;
  onEventMove: (event: MachineEvent, clientX: number) => void;
  onEventDoubleClick: (seg: EventSegment, timestamp: number) => void;
  svgRef: React.RefObject<SVGSVGElement>;
  onResizeStart: (event: MachineEvent, handle: "start" | "end", clientX: number) => void;
  overlappingEventIds: Set<string>;
}

const TimelineTrackComponent: React.FC<TimelineTrackProps> = ({
  track,
  rowIndex,
  yOffset,
  rowHeight,
  barHeight,
  trackKey, // eslint-disable-line @typescript-eslint/no-unused-vars
  scaledWidth,
  min,
  max,
  visibleRange,
  isTrackSelected,
  isEditMode,
  selectedEventForEdit,
  getModifiedEventTimes, // eslint-disable-line @typescript-eslint/no-unused-vars
  hasEventChanges,
  isResizing,
  resizingEvent,
  resizeTempEventTimes,
  lastResizedEventId,
  isMoving,
  movingEvent,
  moveTempEventTimes,
  lastMovedEventId,
  onEventEnter,
  onEventLeave,
  onEventClick,
  onEventOpen,
  onEventMove,
  onEventDoubleClick,
  svgRef,
  onResizeStart,
  overlappingEventIds,
}) => {
  // Pre-compute snap highlights data only when moving (memoized)
  const snapHighlights = useMemo(() => {
    if (!isMoving) return [];

    return track
      .map((seg, i) => {
        const eventLayer = seg.event?.eventLayer?.toLowerCase();
        const eventState = seg.event?.state?.toLowerCase();
        const isDIGLayerByEventLayer = eventLayer && SNAPPABLE_EVENT_LAYERS.includes(eventLayer as any);
        const isDIGLayerByState = eventState && DIG_LAYER_STATES.includes(eventState as any);

        if (!isDIGLayerByEventLayer && !isDIGLayerByState) return null;

        const startX = timeToX(seg.start, min, max, scaledWidth);
        const endX = timeToX(seg.end, min, max, scaledWidth);
        return { key: `snap-highlight-${i}`, startX, width: endX - startX };
      })
      .filter(Boolean) as { key: string; startX: number; width: number }[];
  }, [isMoving, track, min, max, scaledWidth]);

  // Pre-compute selected event ID for comparison
  const selectedUniqueId = useMemo(
    () => selectedEventForEdit ? getEventUniqueId(selectedEventForEdit) : null,
    [selectedEventForEdit]
  );

  // Pre-compute resizing/moving event IDs
  const resizingEventId = useMemo(
    () => resizingEvent ? getEventUniqueId(resizingEvent) : null,
    [resizingEvent]
  );
  const movingEventId = useMemo(
    () => movingEvent ? getEventUniqueId(movingEvent) : null,
    [movingEvent]
  );

  // Memoize the double click handler factory
  const createDoubleClickHandler = useCallback(
    (seg: EventSegment) => (e: React.MouseEvent<SVGRectElement>) => {
      const rect = svgRef.current?.getBoundingClientRect();
      if (!rect) return;
      const clickX = ((e.clientX - rect.left) / rect.width) * scaledWidth;
      const timestamp = min + (clickX / scaledWidth) * (max - min);
      onEventDoubleClick(seg, timestamp);
    },
    [svgRef, scaledWidth, min, max, onEventDoubleClick]
  );

  // Background colors computed once per render
  const bgFill = rowIndex % 2 === 0 ? "var(--timeline-track-even-bg)" : "var(--timeline-track-odd-bg)";
  const bgStroke = rowIndex % 2 === 0 ? "#e5e7eb" : "#d1d5db";

  return (
    <g transform={`translate(0, ${yOffset})`}>
      <rect
        x={0}
        y={0}
        width={scaledWidth}
        height={rowHeight}
        fill={bgFill}
        stroke={bgStroke}
        strokeWidth={0.5}
        rx={2}
      />

      {/* Snap highlight backgrounds - show when moving an event */}
      {snapHighlights.map(({ key, startX, width }) => (
        <rect
          key={key}
          x={startX}
          y={0}
          width={width}
          height={rowHeight}
          fill="rgba(59, 130, 246, 0.12)"
          stroke="rgba(59, 130, 246, 0.3)"
          strokeWidth={1}
          rx={2}
          pointerEvents="none"
        />
      ))}

      {track.map((seg, i) => {
        // Get event unique ID
        const currentEventId = seg.event ? getEventUniqueId(seg.event) : null;

        // Validate segment times
        if (isNaN(seg.start) || isNaN(seg.end)) {
          return null;
        }

        // Segments already have modified times applied from parent (main-preview.tsx)
        const startX = timeToX(seg.start, min, max, scaledWidth);
        const endX = timeToX(seg.end, min, max, scaledWidth);

        if (startX === endX) return null;

        // Off-screen: nothing to draw, nothing to hit-test. Checked before any
        // of the per-segment work below.
        if (visibleRange && (endX < visibleRange.from || startX > visibleRange.to)) return null;

        // Check if this event is truck_cycles and edit mode is active
        const isTruckCycle = seg.trackKey === 'truck_cycles' || seg.trackKey === 'truck-cycle';
        const isEditableInEditMode = isEditMode && isTruckCycle;
        const currentUniqueId = seg.event ? getEventUniqueId(seg.event) : null;
        const isSelectedForEdit = currentUniqueId !== null && currentUniqueId === selectedUniqueId;

        // Use grayed-out color if track is not selected and track lock is active
        const fillColor = isTrackSelected ? seg.color : createGrayedColor(seg.color);
        const strokeColor = isTrackSelected ? createStrokeColor(seg.color) : createGrayedColor(seg.color);

        // Use temp times if this event is being resized or moved
        let displayStartX = startX;
        let displayEndX = endX;

        // Priority: Active operations first, then committed times
        if (isResizing && currentEventId === resizingEventId && resizeTempEventTimes) {
          displayStartX = timeToX(resizeTempEventTimes.start, min, max, scaledWidth);
          displayEndX = timeToX(resizeTempEventTimes.end, min, max, scaledWidth);
        } else if (isMoving && currentEventId === movingEventId && moveTempEventTimes) {
          displayStartX = timeToX(moveTempEventTimes.start, min, max, scaledWidth);
          displayEndX = timeToX(moveTempEventTimes.end, min, max, scaledWidth);
        } else if (currentEventId === lastResizedEventId && resizeTempEventTimes) {
          displayStartX = timeToX(resizeTempEventTimes.start, min, max, scaledWidth);
          displayEndX = timeToX(resizeTempEventTimes.end, min, max, scaledWidth);
        } else if (currentEventId === lastMovedEventId && moveTempEventTimes) {
          displayStartX = timeToX(moveTempEventTimes.start, min, max, scaledWidth);
          displayEndX = timeToX(moveTempEventTimes.end, min, max, scaledWidth);
        }

        const displayWidth = Math.max(1, displayEndX - displayStartX);

        const isOverlapped = currentEventId ? overlappingEventIds.has(currentEventId) : false;

        return (
          <TimelineEventRect
            // Key by event identity, not index: in live mode segments scroll
            // out of the rendered window, and index keys would let React reuse
            // a DOM rect for a different segment — with CSS transitions on
            // x/width that reads as rects teleporting to a neighbor's place.
            key={currentEventId || `${seg.start}-${seg.end}-${i}`}
            seg={seg}
            displayStartX={displayStartX}
            displayEndX={displayEndX}
            displayWidth={displayWidth}
            rowHeight={rowHeight}
            barHeight={barHeight}
            fillColor={fillColor}
            strokeColor={strokeColor}
            isEditMode={isEditMode}
            isTruckCycle={isTruckCycle}
            isSelectedForEdit={isSelectedForEdit}
            isEditableInEditMode={isEditableInEditMode}
            isOverlapped={isOverlapped}
            onEventEnter={onEventEnter}
            onEventLeave={onEventLeave}
            onClick={onEventClick}
            onEventOpen={onEventOpen}
            onMouseDown={onEventMove}
            onDoubleClick={createDoubleClickHandler(seg)}
            hasEventChanges={hasEventChanges}
            onResizeStart={onResizeStart}
          />
        );
      })}
    </g>
  );
};

// Custom equality check for TimelineTrack - only re-render when necessary
const areTrackPropsEqual = (
  prev: TimelineTrackProps,
  next: TimelineTrackProps
): boolean => {
  // Check layout props (cheap comparisons first)
  if (
    prev.rowIndex !== next.rowIndex ||
    prev.yOffset !== next.yOffset ||
    prev.rowHeight !== next.rowHeight ||
    prev.barHeight !== next.barHeight ||
    prev.scaledWidth !== next.scaledWidth ||
    prev.min !== next.min ||
    prev.max !== next.max
  ) {
    return false;
  }

  // The visible window is the thing this track culls against, so a new window
  // has to re-render it. Left out, the cull only ever runs on the renders some
  // OTHER prop happens to trigger — scrolling changes nothing, and a zoom step
  // re-renders with the window read for the PREVIOUS scroll position, in the
  // previous scaled coordinates.
  if (prev.visibleRange !== next.visibleRange) {
    if (!prev.visibleRange || !next.visibleRange) return false;
    if (
      prev.visibleRange.from !== next.visibleRange.from ||
      prev.visibleRange.to !== next.visibleRange.to
    ) {
      return false;
    }
  }

  // Presence, not identity. `MapTimelineRow` swaps this between the handler and
  // `undefined` when a row activates (MapTimelineRow.tsx:266, and again at
  // map-preview-page.tsx:1239), and nothing else compared here changes with it —
  // under a frozen review domain `min`/`max` and `track` all hold still, so the
  // memo kept the `undefined` captured while the row was inactive and clicking a
  // segment did nothing, permanently. Identity would be the wrong test: sibling
  // callbacks are declared inline in the parent and get a new identity every
  // render, so comparing identity would defeat the memo outright.
  if (!prev.onEventOpen !== !next.onEventOpen) return false;

  // Check state props
  if (
    prev.isTrackSelected !== next.isTrackSelected ||
    prev.isEditMode !== next.isEditMode ||
    prev.isResizing !== next.isResizing ||
    prev.isMoving !== next.isMoving
  ) {
    return false;
  }

  // Check if track data changed (by length, then per-segment content so live
  // polls that replace events without changing the count still re-render)
  if (prev.track !== next.track) {
    if (prev.track.length !== next.track.length) {
      return false;
    }
    for (let i = 0; i < prev.track.length; i++) {
      const a = prev.track[i];
      const b = next.track[i];
      if (
        a.start !== b.start ||
        a.end !== b.end ||
        a.color !== b.color ||
        a.hidden !== b.hidden ||
        a.isNew !== b.isNew ||
        (a.event ? getEventUniqueId(a.event) : null) !== (b.event ? getEventUniqueId(b.event) : null)
      ) {
        return false;
      }
    }
  }

  // Check active event changes
  if (prev.selectedEventForEdit !== next.selectedEventForEdit) {
    return false;
  }

  // Check temp event times during drag/resize
  if (prev.resizeTempEventTimes !== next.resizeTempEventTimes) {
    return false;
  }
  if (prev.moveTempEventTimes !== next.moveTempEventTimes) {
    return false;
  }

  // Check active operation targets
  if (prev.resizingEvent !== next.resizingEvent) {
    return false;
  }
  if (prev.movingEvent !== next.movingEvent) {
    return false;
  }

  // Check overlapping event IDs (for overlap preview)
  if (prev.overlappingEventIds !== next.overlappingEventIds) {
    if (prev.overlappingEventIds.size !== next.overlappingEventIds.size) return false;
    for (const id of prev.overlappingEventIds) {
      if (!next.overlappingEventIds.has(id)) return false;
    }
  }

  // Callbacks are assumed stable (should be memoized by parent)
  return true;
};

export const TimelineTrack = memo(TimelineTrackComponent, areTrackPropsEqual);
